import { useState, useMemo, useRef, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { api, type Booking, type Room, type PaymentReminderLogEntry } from '../lib/api';
import { useAuthStore } from '../lib/auth';
import { formatLocalDate, estimateDonationBreakdown } from '../lib/utils';
import {
  X,
  Calendar,
  Clock,
  MapPin,
  User,
  Mail,
  Phone,
  CheckCircle,
  XCircle,
  AlertCircle,
  Shield,
  Ban,
  Check,
  FileText,
  Building2,
  Pencil,
  Trash2,
  AlertTriangle,
  CreditCard,
  Loader2,
  History,
  Heart,
} from 'lucide-react';

interface BookingDetailModalProps {
  booking: Booking;
  room?: Room | null;
  onClose: () => void;
  onUpdate?: () => void;
  // Cross-org context (personal My Bookings page): the booking's own org and display
  // names, so the modal doesn't rely on the currently selected org or org-scoped fetches
  orgSlug?: string;
  roomName?: string;
  buildingName?: string;
}

const statusConfig: Record<string, { label: string; color: string; bg: string; icon: typeof CheckCircle }> = {
  pending: { label: 'Pending Approval', color: 'text-yellow-700', bg: 'bg-yellow-100', icon: AlertCircle },
  confirmed: { label: 'Confirmed', color: 'text-green-700', bg: 'bg-green-100', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'text-gray-700', bg: 'bg-gray-100', icon: XCircle },
  denied: { label: 'Denied', color: 'text-red-700', bg: 'bg-red-100', icon: Ban },
  completed: { label: 'Completed', color: 'text-blue-700', bg: 'bg-blue-100', icon: CheckCircle },
};

const paymentStatusConfig: Record<string, { label: string; color: string; bg: string; icon: typeof CreditCard }> = {
  not_required: { label: 'Free', color: 'text-gray-600', bg: 'bg-gray-100', icon: CheckCircle },
  pending: { label: 'Payment Required', color: 'text-orange-700', bg: 'bg-orange-100', icon: CreditCard },
  paid: { label: 'Paid', color: 'text-green-700', bg: 'bg-green-100', icon: CheckCircle },
  refunded: { label: 'Refunded', color: 'text-blue-700', bg: 'bg-blue-100', icon: CreditCard },
  failed: { label: 'Payment Failed', color: 'text-red-700', bg: 'bg-red-100', icon: AlertCircle },
};

// Colour of the timeline dot per audit action
const historyDotColors: Record<string, string> = {
  create: 'bg-gray-400',
  approve: 'bg-green-500',
  confirm: 'bg-green-500',
  reject: 'bg-red-500',
  deny: 'bg-red-500',
  cancel: 'bg-gray-500',
  complete: 'bg-blue-500',
  update: 'bg-amber-500',
  send: 'bg-blue-400',
};

export function BookingDetailModal({
  booking,
  room: initialRoom,
  onClose,
  onUpdate,
  orgSlug,
  roomName,
  buildingName,
}: BookingDetailModalProps) {
  const { memberships, currentOrg, user } = useAuthStore();

  // The org this booking belongs to - explicit prop wins over the selected org
  const contextOrgSlug = orgSlug || currentOrg?.orgSlug;
  const queryClient = useQueryClient();
  
  // State for actions
  const [showDenyForm, setShowDenyForm] = useState(false);
  const [denyReason, setDenyReason] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showRefundConfirm, setShowRefundConfirm] = useState(false);
  const [refundWithCancel, setRefundWithCancel] = useState(false);
  const [showWaiveConfirm, setShowWaiveConfirm] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  
  // Ref for scrolling confirmation dialogs into view
  const confirmDialogRef = useRef<HTMLDivElement>(null);
  
  // Scroll to confirmation dialog when it appears
  useEffect(() => {
    if ((showCancelConfirm || showRefundConfirm || showDenyForm || showWaiveConfirm) && confirmDialogRef.current) {
      confirmDialogRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showCancelConfirm, showRefundConfirm, showDenyForm, showWaiveConfirm]);
  
  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    title: booking.title || '',
    date: formatLocalDate(new Date(booking.startTime)),
    startTime: new Date(booking.startTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
    endTime: new Date(booking.endTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
    notes: booking.notes || '',
  });
  
  // Check permissions against the booking's own org
  const currentMembership = memberships.find(m => m.orgSlug === contextOrgSlug);
  const isAdmin = currentMembership?.role === 'admin';
  const isApprover = currentMembership?.role === 'approver';
  // Admins and approvers (building/room admins) can manage bookings
  const isManager = isAdmin || isApprover;
  const isOwner = booking.userId === user?.id;
  const canApprove = isManager;
  const canEdit = isManager || isOwner; // Managers AND booking owners can edit
  const canCancel = isManager || isOwner; // Manager or owner
  
  // Fetch room if not provided (skipped when display names were passed in - the
  // org-scoped room/building APIs would miss for bookings in another org)
  const { data: roomData } = useQuery({
    queryKey: ['room', booking.roomId],
    queryFn: () => api.rooms.get(booking.roomId),
    enabled: !initialRoom && !roomName,
  });

  const room = initialRoom || roomData?.room;

  // Fetch building for room
  const { data: buildingsData } = useQuery({
    queryKey: ['buildings'],
    queryFn: () => api.buildings.list(),
    enabled: !roomName,
  });

  const building = buildingsData?.buildings?.find(b => b.id === room?.buildingId);
  const displayRoomName = room?.name || roomName;
  const displayBuildingName = building?.name || buildingName;
  
  // Fetch payment reminders for pending-payment bookings
  const showReminders = booking.paymentStatus === 'pending' && booking.status === 'confirmed'
  const { data: reminderData } = useQuery({
    queryKey: ['booking-reminders', booking.id],
    queryFn: () => api.bookings.getReminders(booking.id),
    enabled: showReminders,
  })

  // Booking history / timeline (admins and approvers only)
  const { data: historyData } = useQuery({
    queryKey: ['booking-history', booking.id],
    queryFn: () => api.bookings.getHistory(booking.id),
    enabled: isManager,
  })
  
  const [sendingReminder, setSendingReminder] = useState(false)
  const [reminderSent, setReminderSent] = useState(false)
  
  // Mutations
  const approveMutation = useMutation({
    mutationFn: async () => {
      return api.bookings.update(booking.id, { status: 'confirmed' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      onUpdate?.();
      onClose();
    },
  });
  
  const denyMutation = useMutation({
    mutationFn: async () => {
      return api.bookings.update(booking.id, { status: 'denied', statusNote: denyReason });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      onUpdate?.();
      onClose();
    },
  });
  
  const cancelMutation = useMutation({
    mutationFn: async () => {
      // For pending bookings, delete; otherwise set to cancelled
      if (booking.status === 'pending') {
        return api.bookings.delete(booking.id);
      }
      return api.bookings.update(booking.id, { status: 'cancelled' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      onUpdate?.();
      onClose();
    },
  });
  
  const updateMutation = useMutation({
    mutationFn: async () => {
      const startDateTime = new Date(`${editForm.date}T${editForm.startTime}`);
      const endDateTime = new Date(`${editForm.date}T${editForm.endTime}`);
      
      // Determine if this is a time change (requires re-approval for non-admins)
      const originalStartStr = new Date(booking.startTime).toISOString();
      const originalEndStr = new Date(booking.endTime).toISOString();
      const newStartStr = startDateTime.toISOString();
      const newEndStr = endDateTime.toISOString();
      const hasTimeChange = originalStartStr !== newStartStr || originalEndStr !== newEndStr;
      
      return api.bookings.update(booking.id, {
        title: editForm.title || undefined,
        startTime: startDateTime.toISOString(),
        endTime: endDateTime.toISOString(),
        notes: editForm.notes || undefined,
        // If a non-manager changes the time, reset to pending for re-approval
        ...(hasTimeChange && !isManager && booking.status === 'confirmed' ? { status: 'pending' as const } : {}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      setIsEditing(false);
      onUpdate?.();
    },
  });
  
  // Payment mutation
  const paymentMutation = useMutation({
    mutationFn: async () => {
      const result = await api.stripe.createCheckoutSession(booking.id);
      // Redirect to Stripe Checkout
      if (result.url) {
        window.location.href = result.url;
      }
      return result;
    },
  });
  
  // Refund mutation (admin only)
  const refundMutation = useMutation({
    mutationFn: async () => {
      return api.stripe.refund(booking.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      setShowRefundConfirm(false);
      onUpdate?.();
    },
  });

  // Voluntary donation (stripe donation mode): how this org collects donations,
  // fetched only when the owner of a voluntary-donation booking might still donate
  const { data: donationContext } = useQuery({
    queryKey: ['donation-context', contextOrgSlug],
    queryFn: () => api.bookings.getDonationContext(contextOrgSlug),
    enabled: !!booking.isVoluntaryDonation && isOwner && !booking.donationPaidAt,
  });

  const donationPledge = booking.donationAmount ? Number(booking.donationAmount) : 0;
  const [donationInput, setDonationInput] = useState(
    donationPledge >= 1 ? String(donationPledge) : ''
  );
  const donationAmount = (() => {
    const value = Math.round(parseFloat(donationInput) * 100) / 100;
    return Number.isFinite(value) && value >= 1 ? Math.min(value, 10000) : 0;
  })();

  const donationMutation = useMutation({
    mutationFn: async () => {
      const result = await api.stripe.createDonationCheckout(booking.id, donationAmount);
      if (result.url) {
        window.location.href = result.url;
      }
      return result;
    },
  });

  // The guest can donate once the booking is approved (or after it has happened)
  const canDonate =
    !!booking.isVoluntaryDonation &&
    isOwner &&
    !booking.donationPaidAt &&
    (booking.status === 'confirmed' || booking.status === 'completed') &&
    donationContext?.donationMode === 'stripe' &&
    donationContext.stripeReady;

  const donationEstimate =
    canDonate && donationAmount > 0 && donationContext
      ? estimateDonationBreakdown(donationAmount, donationContext.roomsiFeePercent)
      : null;

  // Waive payment mutation (admin only) - confirms the booking and marks it paid at £0.00
  const waivePaymentMutation = useMutation({
    mutationFn: async () => {
      return api.bookings.waivePayment(booking.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      setShowWaiveConfirm(false);
      onUpdate?.();
      onClose();
    },
  });
  
  // Format dates
  const startTime = new Date(booking.startTime);
  const endTime = new Date(booking.endTime);
  const dateStr = startTime.toLocaleDateString('en-GB', { 
    weekday: 'long', 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });
  const timeStr = `${startTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} - ${endTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  
  // Calculate duration
  const durationMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60);
  const durationHours = Math.floor(durationMinutes / 60);
  const durationMins = durationMinutes % 60;
  const durationStr = durationHours > 0 
    ? `${durationHours}h${durationMins > 0 ? ` ${durationMins}m` : ''}`
    : `${durationMins}m`;
  
  const isPast = endTime < new Date();
  const isPending = booking.status === 'pending';
  const statusInfo = statusConfig[booking.status] ?? statusConfig['pending']!;
  const StatusIcon = statusInfo.icon;
  
  // Check if current edit form has time changes (for re-approval warning)
  const hasTimeChange = useMemo(() => {
    const originalDate = formatLocalDate(new Date(booking.startTime));
    const originalStartTime = new Date(booking.startTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const originalEndTime = new Date(booking.endTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    
    return editForm.date !== originalDate || 
           editForm.startTime !== originalStartTime || 
           editForm.endTime !== originalEndTime;
  }, [editForm, booking.startTime, booking.endTime]);
  
  // Will this edit require re-approval?
  const willRequireReapproval = hasTimeChange && !isManager && booking.status === 'confirmed';
  
  // Booking source labels
  const getSourceLabel = () => {
    if (booking.bookingSource === 'admin_direct') {
      return { label: 'Admin Booking', icon: Shield, color: 'text-blue-600' };
    } else if (booking.bookingSource === 'guest_request') {
      return { label: 'Guest Request', icon: User, color: 'text-gray-600' };
    }
    return null;
  };
  
  const sourceInfo = getSourceLabel();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`px-6 py-4 border-b ${isPending ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1 flex-wrap">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium ${statusInfo.bg} ${statusInfo.color}`}>
                  <StatusIcon className="w-4 h-4" />
                  {statusInfo.label}
                </span>
                {/* Payment Status Badge - show if booking is confirmed and has payment status */}
                {booking.status === 'confirmed' && booking.paymentStatus && booking.paymentStatus !== 'not_required' && (
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium ${paymentStatusConfig[booking.paymentStatus]?.bg || 'bg-gray-100'} ${paymentStatusConfig[booking.paymentStatus]?.color || 'text-gray-700'}`}>
                    <CreditCard className="w-4 h-4" />
                    {paymentStatusConfig[booking.paymentStatus]?.label || booking.paymentStatus}
                  </span>
                )}
                {sourceInfo && (
                  <span className={`inline-flex items-center gap-1 text-xs ${sourceInfo.color}`}>
                    <sourceInfo.icon className="w-3.5 h-3.5" />
                    {sourceInfo.label}
                  </span>
                )}
              </div>
              <h2 className="text-xl font-semibold text-gray-900 truncate">
                {booking.title || 'Untitled Booking'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Payment Required Banner - show prominently at top for pending payments */}
          {!isEditing && booking.status === 'confirmed' && booking.paymentStatus === 'pending' && (isOwner || isAdmin) && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-md text-amber-700">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <CreditCard className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Payment required</h3>
                    <p className="text-amber-700 text-sm">{booking.recurringPaymentType === 'invoice' ? 'Your invoice for this event is ready to pay' : 'Complete payment to confirm your booking'}</p>
                  </div>
                </div>
                <button
                  onClick={() => paymentMutation.mutate()}
                  disabled={paymentMutation.isPending}
                  className="w-full sm:w-auto px-6 py-3 bg-primary-600 text-white font-semibold rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {paymentMutation.isPending ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Redirecting...
                    </>
                  ) : (
                    <>
                      Pay £{Number(booking.totalPrice).toFixed(2)}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
          
          {isEditing ? (
            // Edit form
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Meeting title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={editForm.date}
                  onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={editForm.startTime}
                    onChange={e => setEditForm(f => ({ ...f, startTime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                  <input
                    type="time"
                    value={editForm.endTime}
                    onChange={e => setEditForm(f => ({ ...f, endTime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={editForm.notes}
                  onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Additional notes..."
                />
              </div>
              
              {/* Re-approval warning for non-admins changing time */}
              {willRequireReapproval && (
                <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">Re-approval Required</h4>
                    <p className="text-sm text-yellow-700 mt-0.5">
                      Changing the date or time will reset your booking to pending and require re-approval from the administrator.
                    </p>
                  </div>
                </div>
              )}
              
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => updateMutation.mutate()}
                  disabled={updateMutation.isPending}
                  className="px-4 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : (
            // View mode
            <div className="space-y-6">
              {/* Date & Time */}
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary-50 rounded-lg">
                  <Calendar className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">{dateStr}</h3>
                  <p className="text-gray-600 flex items-center gap-1.5 mt-0.5">
                    <Clock className="w-4 h-4" />
                    {timeStr}
                    <span className="text-gray-400">·</span>
                    <span className="text-gray-500">{durationStr}</span>
                  </p>
                </div>
              </div>
              
              {/* Room & Location */}
              <div className="flex items-start gap-4">
                <div className="p-3 bg-gray-100 rounded-lg">
                  <MapPin className="w-6 h-6 text-gray-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">{displayRoomName || 'Loading...'}</h3>
                  {displayBuildingName && (
                    <p className="text-gray-600 flex items-center gap-1.5 mt-0.5">
                      <Building2 className="w-4 h-4" />
                      {displayBuildingName}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Requester Info */}
              {booking.bookedBy && (
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gray-100 rounded-lg">
                    <User className="w-6 h-6 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Requested by</h3>
                    <p className="text-gray-600">{booking.bookedBy.name}</p>
                    <p className="text-sm text-gray-500 flex items-center gap-1.5 mt-0.5">
                      <Mail className="w-3.5 h-3.5" />
                      {booking.bookedBy.email}
                    </p>
                    {booking.bookedBy.phone && (
                      <p className="text-sm text-gray-500 flex items-center gap-1.5 mt-0.5">
                        <Phone className="w-3.5 h-3.5" />
                        {booking.bookedBy.phone}
                      </p>
                    )}
                    {booking.bookedBy.role && (
                      <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
                        booking.bookedBy.role === 'admin' ? 'bg-teal-100 text-teal-700' :
                        booking.bookedBy.role === 'approver' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {booking.bookedBy.role.charAt(0).toUpperCase() + booking.bookedBy.role.slice(1)}
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Approval Info */}
              {booking.approvedBy && (
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-50 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {booking.status === 'confirmed' ? 'Approved by' : booking.status === 'denied' ? 'Denied by' : 'Processed by'}
                    </h3>
                    <p className="text-gray-600">{booking.approvedBy.name}</p>
                    {booking.statusChangedAt && (
                      <p className="text-sm text-gray-500 mt-0.5">
                        {new Date(booking.statusChangedAt).toLocaleDateString('en-GB', { 
                          day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                        })}
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Notes */}
              {booking.notes && (
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gray-100 rounded-lg">
                    <FileText className="w-6 h-6 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Notes</h3>
                    <p className="text-gray-600 whitespace-pre-wrap">{booking.notes}</p>
                  </div>
                </div>
              )}
              
              {/* Activity Type Info */}
              {(booking as any).activityTypeName && (
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary-50 rounded-lg">
                    <Shield className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Activity Type</h3>
                    <p className="text-gray-600">{(booking as any).activityTypeName}</p>
                    {(booking as any).activityDescription && (
                      <p className="text-sm text-gray-500 mt-1">{(booking as any).activityDescription}</p>
                    )}
                    {(booking as any).expectedAttendees && (
                      <p className="text-sm text-gray-500 mt-1">
                        Expected attendees: {(booking as any).expectedAttendees}
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* PLI Certificate Info */}
              {(booking as any).pliCertificate && (
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${
                    (booking as any).pliCertificate.isValid 
                      ? 'bg-green-50' 
                      : 'bg-red-50'
                  }`}>
                    <Shield className={`w-6 h-6 ${
                      (booking as any).pliCertificate.isValid 
                        ? 'text-green-600' 
                        : 'text-red-600'
                    }`} />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">PLI Certificate</h3>
                    <p className="text-gray-600">{(booking as any).pliCertificate.insurerName}</p>
                    {(booking as any).pliCertificate.policyNumber && (
                      <p className="text-sm text-gray-500 mt-0.5">
                        Policy: {(booking as any).pliCertificate.policyNumber}
                      </p>
                    )}
                    {(booking as any).pliCertificate.coverageAmount && (
                      <p className="text-sm text-gray-500">
                        Coverage: {(booking as any).pliCertificate.coverageAmount}
                      </p>
                    )}
                    {(booking as any).pliCertificate.validUntil && (
                      <p className={`text-sm mt-0.5 ${
                        new Date((booking as any).pliCertificate.validUntil) < new Date()
                          ? 'text-red-600 font-medium'
                          : 'text-gray-500'
                      }`}>
                        {new Date((booking as any).pliCertificate.validUntil) < new Date()
                          ? 'EXPIRED: '
                          : 'Valid until: '}
                        {new Date((booking as any).pliCertificate.validUntil).toLocaleDateString()}
                      </p>
                    )}
                    <span className={`inline-block mt-1.5 px-2 py-0.5 text-xs rounded-full font-medium ${
                      (booking as any).pliCertificate.isValid
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {(booking as any).pliCertificate.isValid ? 'Valid' : 'Invalid/Expired'}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Terms Accepted Info */}
              {(booking as any).termsAccepted && (booking as any).termsAccepted.length > 0 && (
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Terms Accepted</h3>
                    <div className="space-y-1 mt-1">
                      {(booking as any).termsAccepted.map((term: any) => (
                        <div key={term.id} className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="text-gray-600">{term.title}</span>
                          <span className="text-gray-400">v{term.version}</span>
                        </div>
                      ))}
                    </div>
                    {(booking as any).termsAcceptedAt && (
                      <p className="text-xs text-gray-500 mt-1">
                        Accepted at: {new Date((booking as any).termsAcceptedAt).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {booking.recurringPaymentType === 'invoice' && booking.paymentStatus === 'not_required' && !['cancelled', 'denied'].includes(booking.status) && (
                <p className="mb-4 rounded-md bg-community-soft p-4 text-sm text-community-strong">An invoice for this event will be emailed after use.</p>
              )}
              {/* Payment Info - show for confirmed bookings with payment */}
              {booking.status === 'confirmed' && booking.totalPrice > 0 && booking.paymentStatus && (
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${
                    booking.paymentStatus === 'paid' ? 'bg-green-50' :
                    booking.paymentStatus === 'pending' ? 'bg-orange-50' :
                    booking.paymentStatus === 'failed' ? 'bg-red-50' :
                    booking.paymentStatus === 'refunded' ? 'bg-blue-50' : 'bg-gray-100'
                  }`}>
                    <CreditCard className={`w-6 h-6 ${
                      booking.paymentStatus === 'paid' ? 'text-green-600' :
                      booking.paymentStatus === 'pending' ? 'text-orange-600' :
                      booking.paymentStatus === 'failed' ? 'text-red-600' :
                      booking.paymentStatus === 'refunded' ? 'text-blue-600' : 'text-gray-600'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">Payment</h3>
                    <p className="text-gray-600">
                      £{Number(booking.totalPrice).toFixed(2)}
                      {booking.paymentStatus === 'paid' && (
                        <span className="ml-2 text-green-600 font-medium">• Paid</span>
                      )}
                      {booking.paymentStatus === 'pending' && (
                        <span className="ml-2 text-orange-600 font-medium">• Payment required</span>
                      )}
                      {booking.paymentStatus === 'refunded' && (
                        <span className="ml-2 text-blue-600 font-medium">• Refunded</span>
                      )}
                    </p>
                    {booking.paymentStatus === 'pending' && isOwner && (
                      <p className="text-sm text-orange-700 mt-1">
                        {booking.recurringPaymentType === 'invoice' ? 'Please pay the invoice for this event.' : 'Please complete payment to confirm your booking.'}
                      </p>
                    )}
                    {/* Payment breakdown for admins on paid bookings - only show if we have actual data from Stripe */}
                    {booking.paymentStatus === 'paid' && canApprove && (() => {
                      const breakdown = (booking as any).paymentBreakdown;
                      const totalAmount = Number(booking.totalPrice);
                      
                      // Only show breakdown if we have actual data from Stripe (not null values)
                      if (!breakdown || breakdown.stripeFee === null || breakdown.orgReceives === null) {
                        return (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm space-y-1.5">
                            <div className="flex items-center justify-between text-emerald-700">
                              <span>£{totalAmount.toFixed(2)} Paid by guest</span>
                              <span className="font-medium">✓</span>
                            </div>
                            <p className="text-xs text-gray-400 pt-1">Fee breakdown pending from Stripe</p>
                          </div>
                        );
                      }
                      
                      const stripeFee = breakdown.stripeFee;
                      const roomsiFee = breakdown.roomsiFee;
                      const orgReceives = breakdown.orgReceives;
                      const roomsiFeePercent = breakdown.roomsiFeePercent;
                      
                      return (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm space-y-1.5">
                          <div className="flex items-center justify-between text-emerald-700">
                            <span>£{totalAmount.toFixed(2)} Paid by guest</span>
                            <span className="font-medium">✓</span>
                          </div>
                          <div className="flex items-center justify-between text-gray-500">
                            <span>£{stripeFee.toFixed(2)} Card processing</span>
                            <span>−</span>
                          </div>
                          <div className="flex items-center justify-between text-gray-500">
                            <span>£{roomsiFee.toFixed(2)} Roomsi ({roomsiFeePercent}%)</span>
                            <span>−</span>
                          </div>
                          <div className="flex items-center justify-between text-emerald-700 font-medium border-t border-gray-200 pt-1.5">
                            <span>£{orgReceives.toFixed(2)} to {currentMembership?.orgName || 'you'}</span>
                            <span>✓</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
              
              {/* Voluntary Donation (stripe donation mode) */}
              {booking.isVoluntaryDonation &&
                (booking.donationPaidAt || canDonate || (isManager && donationPledge > 0)) && (
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-rose-50">
                      <Heart className="w-6 h-6 text-rose-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">Voluntary Donation</h3>

                      {booking.donationPaidAt ? (
                        <>
                          <p className="text-gray-600">
                            £{Number(booking.donationAmount || 0).toFixed(2)}
                            <span className="ml-2 text-green-600 font-medium">
                              • Donated, thank you
                            </span>
                          </p>
                          {(() => {
                            const breakdown = booking.donationBreakdown;
                            if (
                              !breakdown ||
                              breakdown.stripeFee === null ||
                              breakdown.orgReceives === null
                            ) {
                              return (
                                <p className="text-xs text-gray-400 mt-1">
                                  Fee breakdown pending from Stripe
                                </p>
                              );
                            }
                            return (
                              <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm space-y-1.5">
                                <div className="flex items-center justify-between text-emerald-700">
                                  <span>£{breakdown.total.toFixed(2)} Donated</span>
                                  <span className="font-medium">✓</span>
                                </div>
                                <div className="flex items-center justify-between text-gray-500">
                                  <span>£{breakdown.stripeFee.toFixed(2)} Card processing</span>
                                  <span>−</span>
                                </div>
                                {(breakdown.roomsiFee || 0) > 0 && (
                                  <div className="flex items-center justify-between text-gray-500">
                                    <span>
                                      £{(breakdown.roomsiFee || 0).toFixed(2)} Roomsi (
                                      {breakdown.roomsiFeePercent}%)
                                    </span>
                                    <span>−</span>
                                  </div>
                                )}
                                <div className="flex items-center justify-between text-emerald-700 font-medium border-t border-gray-200 pt-1.5">
                                  <span>
                                    £{breakdown.orgReceives.toFixed(2)} to{' '}
                                    {currentMembership?.orgName || 'the organisation'}
                                  </span>
                                  <span>✓</span>
                                </div>
                              </div>
                            );
                          })()}
                        </>
                      ) : canDonate ? (
                        <>
                          <p className="text-sm text-gray-600 mb-3">
                            Your booking is free of charge. If you would like to support{' '}
                            {donationContext?.orgName || 'the organisation'}, you can donate by
                            card. There is no obligation.
                          </p>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="relative w-32">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                                £
                              </span>
                              <input
                                type="number"
                                min={1}
                                max={10000}
                                step="0.50"
                                value={donationInput}
                                onChange={(e) => setDonationInput(e.target.value)}
                                placeholder="Amount"
                                className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-400"
                              />
                            </div>
                            <button
                              onClick={() => donationMutation.mutate()}
                              disabled={donationAmount < 1 || donationMutation.isPending}
                              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-rose-500 rounded-lg hover:bg-rose-600 transition-colors disabled:opacity-50"
                            >
                              {donationMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Heart className="w-4 h-4" />
                              )}
                              {donationMutation.isPending
                                ? 'Redirecting...'
                                : donationAmount >= 1
                                  ? `Donate £${donationAmount.toFixed(2)}`
                                  : 'Donate'}
                            </button>
                          </div>
                          {donationEstimate && donationContext && (
                            <div className="p-3 bg-gray-50 rounded-lg text-xs text-gray-600 space-y-1">
                              <div className="flex justify-between">
                                <span>Card fee (estimated)</span>
                                <span>−£{donationEstimate.cardFee.toFixed(2)}</span>
                              </div>
                              {donationEstimate.roomsiFee > 0 && (
                                <div className="flex justify-between">
                                  <span>Roomsi fee ({donationContext.roomsiFeePercent}%)</span>
                                  <span>−£{donationEstimate.roomsiFee.toFixed(2)}</span>
                                </div>
                              )}
                              <div className="flex justify-between font-medium text-gray-900 border-t border-gray-200 pt-1">
                                <span>
                                  {donationContext.orgName || 'The organisation'} receives about
                                </span>
                                <span>£{donationEstimate.orgReceives.toFixed(2)}</span>
                              </div>
                              <p className="text-[11px] text-gray-400 pt-1">
                                Card fees are estimates; the exact amount is set by the card
                                provider.
                              </p>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-gray-600">
                          £{donationPledge.toFixed(2)} pledged
                          <span className="ml-2 text-orange-600 font-medium">• Not yet paid</span>
                        </p>
                      )}
                    </div>
                  </div>
                )}

              {/* Payment Failed Warning */}
              {booking.paymentStatus === 'failed' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="font-medium text-red-800 mb-1 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Payment Failed
                  </h4>
                  <p className="text-red-700 text-sm">
                    Your payment could not be processed. Please try again or contact the venue for assistance.
                  </p>
                </div>
              )}
              
              {/* Payment Reminder Timeline (for pending-payment bookings) */}
              {showReminders && reminderData && (
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg space-y-3">
                  <h4 className="font-medium text-orange-800 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Payment Outstanding
                  </h4>
                  
                  {reminderData.autoCancelDate && (
                    <p className="text-sm text-orange-700">
                      {booking.immediatePaymentDeadline ? (
                        <>This is a late booking. Payment must be received by <strong>{new Date(reminderData.autoCancelDate).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong> or the booking will be automatically cancelled.</>
                      ) : (
                        <>If payment is not received by <strong>{new Date(reminderData.autoCancelDate).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</strong>, this booking will be automatically cancelled.</>
                      )}
                    </p>
                  )}
                  
                  {/* Sent reminders timeline */}
                  {reminderData.reminders.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-medium text-orange-700 mb-1.5">Reminders sent:</p>
                      <div className="space-y-1">
                        {reminderData.reminders.map((r) => (
                          <div key={r.id} className="flex items-center gap-2 text-xs text-orange-600">
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                              r.type === 'auto_cancel' ? 'bg-red-500' :
                              r.type === 'urgent' ? 'bg-orange-500' : 'bg-yellow-500'
                            }`} />
                            <span>
                              {r.type === 'auto_cancel' ? 'Auto-cancelled' :
                               r.type === 'urgent' ? 'Urgent reminder' : 'Reminder'}
                              {' '} - {new Date(r.sentAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              {!r.emailSent && ' (email failed)'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Manual send button (admin only) */}
                  {canApprove && (
                    <button
                      type="button"
                      onClick={async () => {
                        setSendingReminder(true)
                        try {
                          await api.bookings.sendManualReminder(booking.id)
                          setReminderSent(true)
                          queryClient.invalidateQueries({ queryKey: ['booking-reminders', booking.id] })
                          setTimeout(() => setReminderSent(false), 3000)
                        } catch (err) {
                          console.error('Failed to send reminder:', err)
                        } finally {
                          setSendingReminder(false)
                        }
                      }}
                      disabled={sendingReminder || reminderSent}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-orange-300 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors disabled:opacity-50"
                    >
                      {sendingReminder ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : reminderSent ? (
                        <Check className="w-3.5 h-3.5" />
                      ) : (
                        <Mail className="w-3.5 h-3.5" />
                      )}
                      {reminderSent ? 'Reminder Sent' : 'Send Payment Reminder'}
                    </button>
                  )}
                </div>
              )}
              
              {/* Status Note (for denied/cancelled) */}
              {booking.statusNote && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="font-medium text-red-800 mb-1">
                    {booking.status === 'denied' ? 'Reason for Denial' : 'Cancellation Note'}
                  </h4>
                  <p className="text-red-700">{booking.statusNote}</p>
                </div>
              )}
              
              {/* Deny Form */}
              {showDenyForm && (
                <div ref={confirmDialogRef} className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3">
                  <h4 className="font-medium text-red-800">Deny this booking request?</h4>
                  <textarea
                    value={denyReason}
                    onChange={e => setDenyReason(e.target.value)}
                    placeholder="Reason for denial (optional but recommended)"
                    rows={3}
                    className="w-full px-3 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setShowDenyForm(false)}
                      className="px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => denyMutation.mutate()}
                      disabled={denyMutation.isPending}
                      className="px-3 py-1.5 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      {denyMutation.isPending ? 'Denying...' : 'Confirm Deny'}
                    </button>
                  </div>
                </div>
              )}
              
              {/* Cancel Confirm */}
              {showCancelConfirm && (
                <div ref={confirmDialogRef} className="p-4 bg-gray-100 border border-gray-300 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">
                    {isPending ? 'Delete this booking request?' : 'Cancel this booking?'}
                  </h4>
                  <p className="text-sm text-gray-600 mb-3">
                    {isPending
                      ? 'This permanently removes the pending request. The requester will be notified.'
                      : 'This will cancel the confirmed booking. The booker will be notified.'}
                  </p>
                  
                  {/* Refund checkbox for paid bookings (admin only) */}
                  {booking.paymentStatus === 'paid' && isAdmin && (
                    <label className="flex items-center gap-2 mb-3 p-3 bg-teal-50 border border-teal-200 rounded-lg cursor-pointer hover:bg-teal-100">
                      <input
                        type="checkbox"
                        checked={refundWithCancel}
                        onChange={(e) => setRefundWithCancel(e.target.checked)}
                        className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                      />
                      <span className="text-sm text-teal-800">
                        Also refund £{Number(booking.totalPrice).toFixed(2)} to guest
                      </span>
                    </label>
                  )}
                  
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setShowCancelConfirm(false);
                        setRefundWithCancel(false);
                      }}
                      className="px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      {isPending ? 'Keep Request' : 'Keep Booking'}
                    </button>
                    <button
                      onClick={async () => {
                        if (refundWithCancel) {
                          await refundMutation.mutateAsync();
                        }
                        await cancelMutation.mutateAsync();
                        setRefundWithCancel(false);
                      }}
                      disabled={cancelMutation.isPending || refundMutation.isPending}
                      className="px-3 py-1.5 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      {cancelMutation.isPending || refundMutation.isPending
                        ? 'Processing...'
                        : refundWithCancel
                          ? 'Cancel & Refund'
                          : isPending
                            ? 'Yes, Delete'
                            : 'Yes, Cancel'}
                    </button>
                  </div>
                </div>
              )}
              
              {/* Refund Confirm (admin only, standalone refund) */}
              {showRefundConfirm && (
                <div ref={confirmDialogRef} className="p-4 bg-teal-100 border border-teal-300 rounded-lg">
                  <h4 className="font-medium text-teal-900 mb-3">Refund this payment?</h4>
                  <p className="text-sm text-teal-700 mb-3">
                    This will refund £{Number(booking.totalPrice).toFixed(2)} to the guest. The booking will remain active unless you also cancel it.
                  </p>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setShowRefundConfirm(false)}
                      className="px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => refundMutation.mutate()}
                      disabled={refundMutation.isPending}
                      className="px-3 py-1.5 text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50"
                    >
                      {refundMutation.isPending ? 'Refunding...' : 'Confirm Refund'}
                    </button>
                  </div>
                </div>
              )}

              {/* Waive Payment Confirm (admin only) */}
              {showWaiveConfirm && (
                <div ref={confirmDialogRef} className="p-4 bg-green-50 border border-green-300 rounded-lg">
                  <h4 className="font-medium text-green-900 mb-3">Mark as paid (£0.00)?</h4>
                  <p className="text-sm text-green-800 mb-3">
                    This keeps the booking and confirms it as fully paid at £0.00 - no payment will be
                    taken. The guest will be emailed to say their booking is confirmed and nothing is owed.
                    Use this when a 100% discount should apply.
                  </p>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setShowWaiveConfirm(false)}
                      className="px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => waivePaymentMutation.mutate()}
                      disabled={waivePaymentMutation.isPending}
                      className="px-3 py-1.5 text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      {waivePaymentMutation.isPending ? 'Saving...' : 'Confirm - Mark as Paid'}
                    </button>
                  </div>
                </div>
              )}

              {/* Email Form */}
              {showEmailForm && booking.bookedBy && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                  <h4 className="font-medium text-blue-800">Email {booking.bookedBy.name}</h4>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={e => setEmailSubject(e.target.value)}
                    placeholder="Subject"
                    className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <textarea
                    value={emailMessage}
                    onChange={e => setEmailMessage(e.target.value)}
                    placeholder="Your message..."
                    rows={4}
                    className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setShowEmailForm(false)}
                      className="px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <a
                      href={`mailto:${booking.bookedBy.email}?subject=${encodeURIComponent(emailSubject || `Regarding your booking: ${booking.title || 'Room Booking'}`)}&body=${encodeURIComponent(emailMessage)}`}
                      className="px-3 py-1.5 text-white bg-blue-600 rounded-lg hover:bg-blue-700 inline-flex items-center gap-1.5"
                      onClick={() => setShowEmailForm(false)}
                    >
                      <Mail className="w-4 h-4" />
                      Open Email Client
                    </a>
                  </div>
                </div>
              )}

              {/* History / Timeline (admins and approvers only) */}
              {isManager && historyData && historyData.events.length > 0 && (
                <div className="pt-2">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <History className="w-4 h-4 text-gray-500" />
                    History
                  </h3>
                  <div>
                    {historyData.events.map((event, idx) => (
                      <div key={event.id} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${historyDotColors[event.action] || 'bg-gray-400'}`} />
                          {idx < historyData.events.length - 1 && (
                            <div className="w-px flex-1 bg-gray-200 min-h-[1rem]" />
                          )}
                        </div>
                        <div className="pb-4 min-w-0">
                          <p className="text-sm font-medium text-gray-900">{event.label}</p>
                          <p className="text-xs text-gray-500">
                            {event.actor} · {new Date(event.at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                          {event.note && (
                            <p className="text-xs text-gray-600 mt-0.5 italic">“{event.note}”</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer Actions */}
        {!isEditing && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex flex-wrap items-center justify-between gap-3">
              {/* Left side - secondary actions */}
              <div className="flex items-center gap-2">
                {booking.bookedBy && !showEmailForm && !isOwner && (
                  <button
                    onClick={() => setShowEmailForm(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    Email Requester
                  </button>
                )}
                {canEdit && !isPast && booking.status !== 'cancelled' && booking.status !== 'denied' && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                    Edit
                  </button>
                )}
              </div>
              
              {/* Right side - primary actions */}
              <div className="flex items-center gap-2">
                {/* Pay Now button - for confirmed bookings with pending payment */}
                {booking.status === 'confirmed' && booking.paymentStatus === 'pending' && (isOwner || isAdmin) && !showCancelConfirm && !showDenyForm && !showRefundConfirm && !showWaiveConfirm && (
                  <button
                    onClick={() => paymentMutation.mutate()}
                    disabled={paymentMutation.isPending}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {paymentMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CreditCard className="w-4 h-4" />
                    )}
                    {paymentMutation.isPending ? 'Redirecting...' : `Pay Now (£${Number(booking.totalPrice).toFixed(2)})`}
                  </button>
                )}

                {/* Mark as paid / waive payment - admin only, for bookings awaiting payment */}
                {isAdmin && booking.paymentStatus === 'pending' && !showCancelConfirm && !showDenyForm && !showRefundConfirm && !showWaiveConfirm && (
                  <button
                    onClick={() => setShowWaiveConfirm(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                    title="Keep the booking but mark it paid at £0.00 (e.g. a 100% discount)"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Mark as Paid (£0.00)
                  </button>
                )}

                {/* Refund button - admin only, for real Stripe payments */}
                {booking.paymentStatus === 'paid' && booking.stripePaymentIntentId && isAdmin && !showCancelConfirm && !showDenyForm && !showRefundConfirm && !showWaiveConfirm && (
                  <button
                    onClick={() => setShowRefundConfirm(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition-colors"
                  >
                    <CreditCard className="w-4 h-4" />
                    Refund
                  </button>
                )}

                {/* Cancel/Delete button - managers or booking owner. A pending request can
                    always be deleted (even if its date has passed); an active booking can be
                    cancelled when it is not already in the past. */}
                {canCancel && booking.status !== 'cancelled' && booking.status !== 'denied' && (isPending || !isPast) && !showCancelConfirm && !showDenyForm && !showRefundConfirm && !showWaiveConfirm && (
                  <button
                    onClick={() => setShowCancelConfirm(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    {isPending ? 'Delete Request' : 'Cancel Booking'}
                  </button>
                )}
                
                {/* Approval actions - only for pending bookings and users with approval rights */}
                {isPending && canApprove && !isPast && !showDenyForm && !showCancelConfirm && !showRefundConfirm && (
                  <>
                    <button
                      onClick={() => setShowDenyForm(true)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      <Ban className="w-4 h-4" />
                      Deny
                    </button>
                    <button
                      onClick={() => approveMutation.mutate()}
                      disabled={approveMutation.isPending}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      <Check className="w-4 h-4" />
                      {approveMutation.isPending ? 'Approving...' : 'Approve'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
